/**
 * Phonetic Matching Utilities
 * Pure utility functions for word normalization, phonetic matching,
 * and transcript evaluation using Soundex algorithm
 */

import { WORD_PRACTICE_HOMOPHONES } from '@config/speech'

export type MatchStatus = 'exact' | 'homophone' | 'phonetic' | 'mismatch' | 'none'

export interface TranscriptMatchResult {
  status: MatchStatus
  normalizedTranscript: string
  normalizedTarget: string
}

/**
 * Normalizes a word candidate by converting to lowercase,
 * applying Unicode normalization, and removing non-alphabetic characters
 */
export function normalizeWordCandidate(raw: string): string {
  if (typeof raw !== 'string') {
    return ''
  }
  return raw
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z]/g, '')
}

/**
 * Builds a homophone lookup map from the configured homophones
 */
export function buildHomophoneLookup(): Record<string, string[]> {
  return Object.keys(WORD_PRACTICE_HOMOPHONES).reduce(
    (map, key) => {
      const normalizedKey = normalizeWordCandidate(key)
      if (!normalizedKey) {
        return map
      }
      const variants = (WORD_PRACTICE_HOMOPHONES[key] || [])
        .concat(key)
        .map(entry => normalizeWordCandidate(entry))
        .filter(Boolean)
      map[normalizedKey] = Array.from(new Set(variants))
      return map
    },
    {} as Record<string, string[]>
  )
}

// Build the lookup once at initialization
const HOMOPHONE_LOOKUP = buildHomophoneLookup()

/**
 * Computes the Soundex code for a word using the standard Soundex algorithm.
 * IMPORTANT: This algorithm must be preserved exactly as is - do not modify
 */
export function computeSoundex(word: string): string {
  if (!word) {
    return ''
  }

  const upper = word.toUpperCase()
  const firstChar = upper[0]

  // Consonant sound mapping
  const codes: Record<string, string> = {
    B: '1',
    F: '1',
    P: '1',
    V: '1',
    C: '2',
    G: '2',
    J: '2',
    K: '2',
    Q: '2',
    S: '2',
    X: '2',
    Z: '2',
    D: '3',
    T: '3',
    L: '4',
    M: '5',
    N: '5',
    R: '6',
  }

  let result = firstChar
  let previousCode = codes[firstChar] || ''

  // Process remaining characters
  for (let i = 1; i < upper.length && result.length < 4; i += 1) {
    const char = upper[i]

    // Skip H and W
    if (char === 'H' || char === 'W') {
      continue
    }

    const code = codes[char] || ''

    // Add code if it's different from the previous one
    if (code && code !== previousCode) {
      result += code
    }

    previousCode = code || ''
  }

  // Pad with zeros to make it 4 characters
  while (result.length < 4) {
    result += '0'
  }

  return result
}

/**
 * Evaluates how well a transcript matches a target word
 */
export function evaluateTranscriptMatch(
  transcript: string,
  target: string
): TranscriptMatchResult {
  const normalizedTranscript = normalizeWordCandidate(transcript)
  const normalizedTarget = normalizeWordCandidate(target)

  // Check for empty input
  if (!normalizedTranscript || !normalizedTarget) {
    return {
      status: 'none',
      normalizedTranscript,
      normalizedTarget,
    }
  }

  // Check for exact match
  if (normalizedTranscript === normalizedTarget) {
    return {
      status: 'exact',
      normalizedTranscript,
      normalizedTarget,
    }
  }

  // Check for homophones
  const homophones = HOMOPHONE_LOOKUP[normalizedTarget]
  if (homophones && homophones.includes(normalizedTranscript)) {
    return {
      status: 'homophone',
      normalizedTranscript,
      normalizedTarget,
    }
  }

  // Check for phonetic similarity using Soundex
  const transcriptSoundex = computeSoundex(normalizedTranscript)
  const targetSoundex = computeSoundex(normalizedTarget)
  if (transcriptSoundex && transcriptSoundex === targetSoundex) {
    return {
      status: 'phonetic',
      normalizedTranscript,
      normalizedTarget,
    }
  }

  // No match found
  return {
    status: 'mismatch',
    normalizedTranscript,
    normalizedTarget,
  }
}
